import React from 'react';
import { Check, ArrowRight, Forklift, Factory, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const TractionBatteryPage = () => {
  const specs = [
    { label: 'Chemistry', value: 'Multi-Ion' },
    { label: 'Voltage Range', value: '48V - 80V' },
    { label: 'Capacity', value: '100Ah - 500Ah' },
    { label: 'Cycle Life', value: '5,000+ cycles' },
    { label: 'Fast Charge', value: '15 minutes' },
    { label: 'Operating Temp', value: '-40°C to +65°C' },
    { label: 'Warranty', value: '7 years' },
    { label: 'BMS', value: 'Cloud connected' },
  ];

  const applications = [
    { name: 'Forklifts', icon: Forklift, description: 'Electric forklifts and reach trucks' },
    { name: 'Material Handling', icon: Factory, description: 'AGVs, pallet jacks, order pickers' },
    { name: 'Industrial Vehicles', icon: Truck, description: 'Tow tractors, utility vehicles' },
  ];

  const features = [
    '15-minute opportunity charging eliminates battery swaps',
    'Drop-in replacement for lead-acid batteries',
    '5,000+ cycles reduces replacement frequency by 60%',
    'Operates reliably in cold storage (-40°C)',
    'Cloud-connected BMS for fleet management',
    'Zero maintenance requirements',
    '40% lower total cost of ownership',
    '7-year warranty',
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title="Traction Battery India | Forklift Battery | Industrial Material Handling"
        description="Industrial traction batteries for forklifts, cranes, and warehouse equipment in India. 15-minute fast charging, 5000+ cycle life, -40°C to 65°C operation. Multi-Ion technology for cold storage and logistics."
        keywords="traction battery India, forklift battery, crane battery, warehouse battery, industrial battery, material handling battery, cold storage battery, electric forklift battery, 48V battery, 80V battery, fast charging forklift battery"
        canonicalPath="/products/traction-battery"
      />
      
      {/* Hero */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/29491416/pexels-photo-29491416.jpeg?auto=compress&cs=tinysrgb&w=800" 
            alt=""
            className="w-full h-full object-cover opacity-20"
            loading="eager"
            fetchpriority="high"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6 sm:mb-8 min-h-[44px]">
            <ArrowRight className="w-4 h-4 rotate-180" aria-hidden="true" /> Back to Products
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">
            Traction <span className="text-emerald-400">Battery</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mb-6 sm:mb-8">
            High-performance Multi-Ion battery systems for industrial material handling equipment. Forklift batteries, crane batteries, and warehouse mobility. Charge in 15 minutes, last 5,000+ cycles.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-all min-h-[48px] w-full sm:w-auto"
          >
            Request Specifications <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* Specs & Features */}
      <section className="py-12 sm:py-20 bg-gray-950" aria-labelledby="specs-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Specifications */}
            <div>
              <h2 id="specs-heading" className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Technical Specifications</h2>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {specs.map((spec) => (
                  <div key={spec.label} className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
                    <p className="text-gray-500 text-xs sm:text-sm mb-1">{spec.label}</p>
                    <p className="text-white font-semibold text-sm sm:text-base">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Key Features</h2>
              <div className="space-y-3 sm:space-y-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <p className="text-gray-300">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Applications</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {applications.map((app) => {
              const Icon = app.icon;
              return (
                <div key={app.name} className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700 text-center">
                  <div className="w-16 h-16 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-semibold text-xl mb-2">{app.name}</h3>
                  <p className="text-gray-500">{app.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Field Validation Section */}
      <section className="relative py-24 bg-gray-950 overflow-hidden">
        {/* Subtle Background Texture */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-800 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">
                Gradient-Tested in Real-World Industrial Operations
              </h2>
              <p className="text-gray-500 text-lg mb-12">
                Battery performance validated under sustained incline load in live warehouse environments
              </p>
              
              {/* Validation Facts */}
              <div className="space-y-6">
                <div className="flex items-baseline gap-4 border-b border-gray-800 pb-4">
                  <span className="text-gray-500 text-sm uppercase tracking-wider w-44 shrink-0">Application</span>
                  <span className="text-white">Electric Forklift</span>
                </div>
                <div className="flex items-baseline gap-4 border-b border-gray-800 pb-4">
                  <span className="text-gray-500 text-sm uppercase tracking-wider w-44 shrink-0">Load</span>
                  <span className="text-white">3 Metric Tons</span>
                </div>
                <div className="flex items-baseline gap-4 border-b border-gray-800 pb-4">
                  <span className="text-gray-500 text-sm uppercase tracking-wider w-44 shrink-0">Operating Condition</span>
                  <span className="text-white">Repeated incline climbs</span>
                </div>
                <div className="flex items-baseline gap-4 border-b border-gray-800 pb-4">
                  <span className="text-gray-500 text-sm uppercase tracking-wider w-44 shrink-0">Gradient</span>
                  <span className="text-white">18–20°</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-gray-500 text-sm uppercase tracking-wider w-44 shrink-0">Performance</span>
                  <span className="text-white">Stable power delivery under sustained load</span>
                </div>
              </div>
              
              {/* Confidence Line */}
              <p className="mt-12 text-gray-400 text-sm border-l-2 border-gray-700 pl-4">
                Designed for sustained high-torque operation in demanding industrial conditions.
              </p>
            </div>
            
            {/* Visual */}
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/images/forklift-gradient-real.png" 
                  alt="Industrial forklift under operational load on gradient"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-gray-950/30" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to upgrade your fleet?</h2>
          <p className="text-gray-400 text-lg mb-8">
            Contact our engineering team for custom specifications and pricing.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Talk to Engineering <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TractionBatteryPage;
